// ManageGroups.js
import React, { useState, useEffect } from 'react';

const emptyForm = { name: '', displayName: '', description: '', roles: [] };

const ManageGroups = ({ backendUrl, resetKey }) => {
  const [groups, setGroups] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchGroups();
    fetchAvailableRoles();
    setSelectedGroup(null);
    setIsCreating(false);
    setForm(emptyForm);
    // eslint-disable-next-line
  }, [resetKey]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'auth-token': localStorage.getItem('auth-token'),
  });

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${backendUrl}/groups`, { headers: authHeaders() });
      const result = await response.json();
      if (result.success) setGroups(result.groups);
      else console.error('Error fetching groups:', result.message);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const response = await fetch(`${backendUrl}/groups/available-roles`, { headers: authHeaders() });
      const result = await response.json();
      if (result.success) setAvailableRoles(result.roles);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const startCreate = () => {
    setForm(emptyForm);
    setIsCreating(true);
    setSelectedGroup(null);
  };

  const startEdit = (group) => {
    setSelectedGroup(group);
    setForm({
      name: group.name,
      displayName: group.displayName,
      description: group.description || '',
      roles: (group.roles || []).map((r) => (typeof r === 'object' ? r._id : r)),
    });
    setIsCreating(false);
  };

  const backToList = () => {
    setSelectedGroup(null);
    setIsCreating(false);
    setForm(emptyForm);
  };

  const toggleRole = (roleId) => {
    setForm((prev) => {
      const has = prev.roles.includes(roleId);
      return { ...prev, roles: has ? prev.roles.filter((r) => r !== roleId) : [...prev.roles, roleId] };
    });
  };

  const createGroup = async () => {
    if (!form.name || !form.displayName) {
      alert('Name and Display Name are required.');
      return;
    }
    try {
      const response = await fetch(`${backendUrl}/groups`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const result = await response.json();
      if (result.success) {
        alert('Group created successfully!');
        backToList();
        fetchGroups();
      } else {
        alert(result.message || 'Error creating group.');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert('An error occurred while creating the group.');
    }
  };

  const updateGroup = async () => {
    if (!selectedGroup) return;
    try {
      const response = await fetch(`${backendUrl}/groups/${selectedGroup._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          displayName: form.displayName,
          description: form.description,
          roles: form.roles,
        }),
      });
      const result = await response.json();
      if (result.success) {
        alert('Group updated successfully!');
        backToList();
        fetchGroups();
      } else {
        alert(result.message || 'Error updating group.');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      alert('An error occurred while updating the group.');
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Delete this group? It will be removed from all its members.')) return;
    try {
      const response = await fetch(`${backendUrl}/groups/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const result = await response.json();
      if (result.success) {
        alert('Group deleted successfully!');
        backToList();
        fetchGroups();
      } else {
        alert(result.message || 'Error deleting group.');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('An error occurred while deleting the group.');
    }
  };

  const renderForm = () => (
    <div className="form-group">
      <h3>{isCreating ? 'Create New Group' : `Edit Group: ${selectedGroup.displayName}`}</h3>

      {isCreating && (
        <>
          <label htmlFor="name">Name (internal id, lowercase, no spaces)</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. cliente-acme"
          />
        </>
      )}

      <label htmlFor="displayName">Display Name</label>
      <input
        type="text"
        value={form.displayName}
        onChange={(e) => setForm({ ...form, displayName: e.target.value })}
        placeholder="e.g. Cliente Acme"
      />

      <label htmlFor="description">Description</label>
      <input
        type="text"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="What this group is for"
      />

      <label>Roles (members inherit these)</label>
      {availableRoles.length === 0 ? (
        <p style={{ color: '#a0aec0' }}>No roles available. Create roles in "Manage Roles" first.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' }}>
          {availableRoles.map((role) => (
            <label key={role._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={form.roles.includes(role._id)}
                onChange={() => toggleRole(role._id)}
                style={{ width: 'auto', margin: 0 }}
              />
              {role.displayName}
              <span style={{ color: '#718096', fontSize: '12px' }}>({role.name})</span>
            </label>
          ))}
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        {isCreating ? (
          <button onClick={createGroup}>Create Group</button>
        ) : (
          <button onClick={updateGroup}>Save Changes</button>
        )}
        {!isCreating && selectedGroup && (
          <button style={{ backgroundColor: 'red' }} onClick={() => deleteGroup(selectedGroup._id)}>
            Delete Group
          </button>
        )}
        <button onClick={backToList}>Back to List</button>
      </div>
    </div>
  );

  return (
    <div className="form-group">
      <h2>Manage Groups</h2>
      <p style={{ color: '#a0aec0', fontSize: '13px', marginTop: 0 }}>
        Groups bundle roles. Members inherit the group's roles automatically — assign members in "Manage Users".
      </p>

      {isCreating || selectedGroup ? (
        renderForm()
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Display Name</th>
                <th>Name</th>
                <th style={{ textAlign: 'center' }}>Roles</th>
                <th style={{ textAlign: 'center' }}>Members</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group._id} onClick={() => startEdit(group)}>
                  <td>{group.displayName}</td>
                  <td>{group.name}</td>
                  <td style={{ textAlign: 'center' }}>
                    {group.roles && group.roles.length > 0
                      ? group.roles.map((r) => (typeof r === 'object' ? r.displayName : r)).join(', ')
                      : <span style={{ color: '#a0aec0' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>{group.memberCount ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button style={{ marginTop: '12px' }} onClick={startCreate}>Create New Group</button>
        </>
      )}
    </div>
  );
};

export default ManageGroups;
